<template>
  <v-card flat height="100%" class="transparent d-flex flex-column">
    
    <v-card-title class="grey darken-4 white--text elevation-3 mb-4 py-3">
      <v-icon left color="blue accent-2">mdi-calendar-star</v-icon>
      <span class="text-h6 font-weight-bold">Spezialtage & Events</span>
    </v-card-title>
    
    <v-card-text class="pa-0 flex-grow-1 custom-scrollbar" style="overflow-y: auto;">
      
      <v-container fluid class="pa-4" style="max-width: 1400px;">
        
        <v-alert 
            border="left" 
            colored-border 
            color="blue accent-2" 
            elevation="2" 
            class="mb-6 grey darken-3 white--text rounded-lg"
            icon="mdi-information-outline"
        >
            <div class="subtitle-2 font-weight-bold mb-1 blue--text text--accent-2">Planungs-Modus wählen</div>
            <div class="text-caption grey--text text--lighten-2" style="line-height: 1.4;">
                <strong>Ersetzen:</strong> Löscht den Tagesplan. Ideal für reduzierte Besetzung oder geschlossene Events.<br>
                <strong>Zusatz:</strong> Behält den Standard bei. Ideal für zusätzliche Verstärkung (Promo, Event).
            </div>
        </v-alert>

        <v-alert
            v-if="publicHolidaysInRange.length > 0"
            border="left"
            colored-border
            color="amber darken-1"
            elevation="2"
            class="mb-6 grey darken-3 white--text rounded-lg"
            icon="mdi-star-four-points"
        >
            <div class="subtitle-2 font-weight-bold mb-1 amber--text text--darken-1">
              🎉 {{ publicHolidaysInRange.length }} Feiertag{{ publicHolidaysInRange.length > 1 ? 'e' : '' }} in diesem Zeitraum
            </div>
            <div class="text-caption grey--text text--lighten-2" style="line-height: 1.6;">
              Das Center ist offen, aber du kannst einen <strong>Spezialtag</strong> planen falls du weniger Personal brauchst.
            </div>
            <div class="mt-2 d-flex flex-wrap" style="gap: 8px;">
              <v-chip
                v-for="hol in publicHolidaysInRange"
                :key="hol.date"
                small
                label
                :color="hol.isWeekend ? 'grey darken-2' : 'amber darken-4'"
                :text-color="hol.isWeekend ? 'grey lighten-1' : 'amber lighten-3'"
                class="font-weight-bold"
                @click="!hol.isWeekend && openDialogForHoliday(hol)"
                :disabled="hol.isWeekend"
              >
                <v-icon left x-small>{{ hol.isWeekend ? 'mdi-calendar-weekend' : 'mdi-star-four-points' }}</v-icon>
                {{ $moment(hol.date).format('dd, DD.MM') }} – {{ hol.name }}{{ hol.isWeekend ? ' (WE)' : '' }}
                <v-icon v-if="!hol.isWeekend" right x-small class="ml-2">mdi-arrow-right</v-icon>
              </v-chip>
            </div>
        </v-alert>

        <div v-if="loading" class="text-center my-5">
            <v-progress-circular indeterminate color="blue accent-2"></v-progress-circular>
            <div class="text-caption grey--text mt-2">Lade vorhandene Spezialtage...</div>
        </div>

        <div v-else-if="specialDays.length > 0" class="mb-8">
            <div class="text-overline grey--text text--lighten-1 mb-2 ml-1">Bereits erstellt</div>
            <v-row dense>
                <v-col v-for="(day, index) in specialDays" :key="index" cols="12" sm="6" md="4" lg="3">
                    <v-card outlined class="grey darken-3 border-subtle hover-glow rounded-lg">
                        <div class="d-flex align-center pa-3">
                            <div class="text-center white--text px-3 py-1 rounded grey darken-4 mr-3 border-subtle">
                                <div class="text-h5 font-weight-bold">{{ $moment(day.date).format('DD') }}</div>
                                <div class="text-caption text-uppercase font-weight-bold grey--text">{{ $moment(day.date).format('MMM') }}</div>
                            </div>
                            <div class="flex-grow-1 overflow-hidden">
                                <v-chip x-small label :color="day.mode === 'replace' ? 'orange darken-4' : 'blue darken-4'" class="mb-1 font-weight-bold px-2">
                                    {{ day.mode === 'replace' ? 'ERSATZ' : 'ZUSATZ' }}
                                </v-chip>
                                <div class="text-caption grey--text text--lighten-1 text-truncate">
                                    {{ day.shifts.length }} Schichten ({{ day.shifts.filter(s => s.assignedUserId).length }} fix)
                                </div>
                            </div>
                            
                            <div class="d-flex flex-column">
                                <v-btn icon color="blue lighten-3" @click="editDay(day)" small class="mb-1">
                                    <v-icon small>mdi-pencil</v-icon>
                                </v-btn>
                                <v-btn icon color="red lighten-2" @click="deleteDay(day)" small :loading="deleting === day.date">
                                    <v-icon small>mdi-delete</v-icon>
                                </v-btn>
                            </div>
                        </div>
                    </v-card>
                </v-col>
            </v-row>
        </div>
        
        <div v-else class="text-center py-4 grey--text text--darken-1">
            Noch keine Spezialtage für diesen Zeitraum definiert.
        </div>

        <div class="text-center my-6">
            <v-btn color="primary" depressed rounded large @click="openDialog(null)" class="px-6">
                <v-icon left>mdi-calendar-plus</v-icon> Neuen Spezialtag planen
            </v-btn>
        </div>

      </v-container>
    </v-card-text>

    <v-card-actions class="justify-space-between grey darken-4 border-top-subtle pa-4 elevation-10">
       <v-btn text color="grey lighten-1" @click="$emit('back')">
           <v-icon left small>mdi-arrow-left</v-icon> Zurück
       </v-btn>
       <v-btn color="success" depressed large @click="$emit('next')">
           Weiter zur Zuweisung <v-icon right small>mdi-arrow-right</v-icon>
       </v-btn>
    </v-card-actions>

    <v-dialog v-model="dialog" fullscreen transition="dialog-bottom-transition" dark hide-overlay>
        <v-card class="grey darken-4 d-flex flex-column">
            
            <v-toolbar color="grey darken-4" elevation="1" class="border-bottom-subtle flex-grow-0">
                <v-btn icon @click="dialog = false" class="mr-2"><v-icon>mdi-close</v-icon></v-btn>
                <v-toolbar-title class="font-weight-bold pl-0">
                    <span class="blue--text text--accent-2 mr-2">Spezialtag</span> Konfigurator
                </v-toolbar-title>
                <v-spacer></v-spacer>
                <div class="d-flex align-center">
                    <span class="text-caption grey--text mr-4 d-none d-sm-block" v-if="currentConfig.date">
                        {{ totalShiftsCount }} Schichten • {{ totalHours.toFixed(1) }}h Total
                    </span>
                    <v-btn color="success" depressed @click="saveCurrentDay" :loading="saving" :disabled="!currentConfig.date">
                        <v-icon left>mdi-content-save-check</v-icon> Speichern
                    </v-btn>
                </div>
            </v-toolbar>

            <v-card-text class="pa-0 flex-grow-1 d-flex grey darken-4" style="overflow: hidden;">
                <v-row no-gutters class="fill-height">
                    <v-col cols="12" md="3" lg="2" class="border-right-subtle d-flex flex-column grey darken-4 fill-height" style="overflow-y: auto;">
                        <div class="pa-4">
                            <div class="text-caption font-weight-bold grey--text text--darken-1 mb-2 text-uppercase">1. Wann?</div>
                            <v-menu v-model="menuDate" :close-on-content-click="false" transition="scale-transition" offset-y min-width="auto">
                                <template v-slot:activator="{ on, attrs }">
                                    <v-text-field
                                        v-model="currentConfig.date"
                                        prepend-inner-icon="mdi-calendar"
                                        readonly
                                        outlined
                                        dense
                                        hide-details
                                        class="rounded-lg mb-6 font-weight-bold centered-input-text"
                                        background-color="grey darken-3"
                                        placeholder="Datum wählen"
                                        v-bind="attrs"
                                        v-on="on"
                                        @change="onDateOrModeChange"
                                    ></v-text-field>
                                </template>
                                <v-date-picker 
                                    v-model="currentConfig.date" 
                                    @input="onDateSelected"
                                    :min="planningStartDate"
                                    :max="planningEndDate"
                                    no-title
                                    dark
                                    color="blue accent-2"
                                ></v-date-picker>
                            </v-menu>

                            <div v-if="currentConfig.date" class="fade-in">
                                <div class="text-caption font-weight-bold grey--text text--darken-1 mb-2 text-uppercase">2. Was?</div>
                                <v-item-group v-model="currentConfig.mode" mandatory class="d-flex flex-column" @change="onDateOrModeChange">
                                    <v-item v-slot="{ active, toggle }" value="replace">
                                        <v-card 
                                            flat 
                                            class="mb-3 pa-3 cursor-pointer transition-swing rounded-lg"
                                            :class="active ? 'mode-card-active-replace' : 'grey darken-3 border-subtle'"
                                            @click="toggle"
                                        >
                                            <div class="d-flex align-center mb-1">
                                                <v-icon small :color="active ? 'blue accent-2' : 'grey'" class="mr-2">mdi-refresh</v-icon>
                                                <div class="font-weight-bold text-body-2" :class="active ? 'white--text' : 'grey--text'">Override</div>
                                            </div>
                                            <div class="text-caption" :class="active ? 'blue--text text--lighten-4' : 'grey--text text--darken-1'" style="line-height: 1.2;">
                                                Ersetzt den Standard. Alles neu.
                                            </div>
                                        </v-card>
                                    </v-item>

                                    <v-item v-slot="{ active, toggle }" value="add">
                                        <v-card 
                                            flat 
                                            class="pa-3 cursor-pointer transition-swing rounded-lg"
                                            :class="active ? 'mode-card-active-add' : 'grey darken-3 border-subtle'"
                                            @click="toggle"
                                        >
                                            <div class="d-flex align-center mb-1">
                                                <v-icon small :color="active ? 'green accent-3' : 'grey'" class="mr-2">mdi-plus-circle</v-icon>
                                                <div class="font-weight-bold text-body-2" :class="active ? 'white--text' : 'grey--text'">Add-On</div>
                                            </div>
                                            <div class="text-caption" :class="active ? 'green--text text--lighten-4' : 'grey--text text--darken-1'" style="line-height: 1.2;">
                                                Zusätzlich zum Standard.
                                            </div>
                                        </v-card>
                                    </v-item>
                                </v-item-group>
                            </div>

                            <v-card v-if="currentConfig.date" flat class="mt-8 pa-4 grey darken-3 rounded-lg border-subtle">
                                <div class="d-flex justify-space-between mb-2 text-caption grey--text">
                                    <span>Schichten:</span>
                                    <strong class="white--text">{{ totalShiftsCount }}</strong>
                                </div>
                                <div class="d-flex justify-space-between mb-0 text-caption grey--text">
                                    <span>Stunden:</span>
                                    <strong class="white--text">{{ totalHours.toFixed(1) }}h</strong>
                                </div>
                                <v-divider class="my-2 grey darken-2"></v-divider>
                                <div v-if="currentConfig.mode === 'add'" class="text-caption green--text text--darken-1">
                                    + {{ standardShiftsForDate.length }} Standard
                                </div>
                            </v-card>
                        </div>
                    </v-col>

                    <v-col cols="12" md="9" lg="10" class="fill-height grey darken-4 d-flex flex-column relative">
                        
                        <div v-if="!currentConfig.date" class="fill-height d-flex flex-column justify-center align-center opacity-50">
                            <v-icon size="64" color="grey darken-2" class="mb-4">mdi-calendar-cursor</v-icon>
                            <div class="text-h6 grey--text text--darken-1">Wähle zuerst links ein Datum</div>
                        </div>

                        <div v-else class="fill-height d-flex flex-column">
                            
                            <div class="px-6 py-4 border-bottom-subtle d-flex justify-space-between align-center bg-stripes">
                                <div>
                                    <div class="text-h6 font-weight-bold white--text d-flex align-center">
                                        {{ $moment(currentConfig.date).format('dddd, DD. MMMM YYYY') }}
                                        <v-chip x-small class="ml-3 font-weight-bold" :color="currentConfig.mode === 'replace' ? 'blue darken-3' : 'green darken-3'">
                                            {{ currentConfig.mode === 'replace' ? 'OVERRIDE MODUS' : 'ADD-ON MODUS' }}
                                        </v-chip>
                                    </div>
                                    <div class="text-caption grey--text mt-1">
                                        {{ currentConfig.mode === 'replace' ? 'Definiere den kompletten Tagesablauf neu.' : 'Füge zusätzliche Schichten zum bestehenden Plan hinzu.' }}
                                    </div>
                                </div>
                                <v-btn color="primary" depressed @click="addShift" class="px-4">
                                    <v-icon left>mdi-plus</v-icon> Schicht
                                </v-btn>
                            </div>

                            <div class="flex-grow-1 overflow-y-auto pa-6 custom-scrollbar">
                                
                                <v-expand-transition>
                                    <div v-if="currentConfig.mode === 'add' && standardShiftsForDate.length > 0" class="mb-8">
                                        <div class="text-overline grey--text text--darken-1 mb-2 ml-1 d-flex align-center">
                                            <v-icon x-small left color="grey darken-1">mdi-lock</v-icon> 
                                            Standard-Schichten (bleiben erhalten)
                                        </div>
                                        <v-row dense>
                                            <v-col v-for="(std, idx) in standardShiftsForDate" :key="'std'+idx" cols="12" sm="6" md="4" lg="3">
                                                <v-card flat outlined class="grey darken-3 border-subtle pa-2 d-flex align-center opacity-60">
                                                    <v-chip x-small color="grey darken-2" class="mr-2 white--text font-weight-bold">{{ getShiftTimeRange(std) }}</v-chip>
                                                    <span class="text-caption grey--text text--lighten-1 text-truncate">
                                                        {{ getShiftName(std.shiftTemplateId) }}
                                                    </span>
                                                </v-card>
                                            </v-col>
                                        </v-row>
                                    </div>
                                </v-expand-transition>

                                <div v-if="currentConfig.shifts.length === 0" class="d-flex flex-column align-center justify-center pa-10 border-dashed rounded-lg mt-4">
                                    <v-icon size="48" color="grey darken-2" class="mb-2">mdi-playlist-plus</v-icon>
                                    <div class="grey--text text--darken-1">Noch keine Schichten erstellt.</div>
                                    <v-btn v-if="currentConfig.mode === 'replace'" text color="blue accent-2" class="mt-2" small @click="loadStandardShifts">
                                        Standard-Vorlage laden
                                    </v-btn>
                                </div>

                                <v-slide-y-transition group>
                                    <v-card v-for="(shift, i) in currentConfig.shifts" :key="i" outlined class="mb-4 shift-card grey darken-3 elevation-2">
                                        
                                        <div class="shift-card-header px-4 py-2 d-flex align-center">
                                            <div class="d-flex align-center flex-grow-1">
                                                <v-icon small color="grey lighten-1" class="mr-3">mdi-clock-outline</v-icon>
                                                <div style="width: 300px;">
                                                    <v-select
                                                        :items="availableShifts"
                                                        v-model="shift.shiftTemplateId"
                                                        placeholder="Vorlage wählen (optional)"
                                                        item-text="name"
                                                        item-value="_id"
                                                        dense flat solo-inverted hide-details
                                                        background-color="grey darken-2"
                                                        class="small-select font-weight-bold"
                                                        @change="(val) => applyTemplate(shift, val)"
                                                    >
                                                        <template v-slot:item="{ item }">
                                                            <v-chip x-small :color="item.color" class="mr-2"></v-chip> {{ item.name }}
                                                        </template>
                                                        <template v-slot:selection="{ item }">
                                                            <v-chip x-small :color="item.color" class="mr-2" label></v-chip> {{ item.name }}
                                                        </template>
                                                    </v-select>
                                                </div>
                                            </div>
                                            
                                            <div class="d-flex">
                                                <v-btn icon small color="grey" @click="copyShift(shift)" title="Duplizieren">
                                                    <v-icon small>mdi-content-copy</v-icon>
                                                </v-btn>
                                                <v-btn icon small color="red lighten-2" @click="removeShift(i)" title="Löschen">
                                                    <v-icon small>mdi-delete-outline</v-icon>
                                                </v-btn>
                                            </div>
                                        </div>

                                        <div class="pa-4">
                                            <v-row>
                                                <v-col cols="12" md="7" lg="6" class="border-right-responsive pr-md-6">
                                                    <div class="d-flex justify-space-between align-center mb-3">
                                                        <span class="text-caption font-weight-bold grey--text text--darken-1 text-uppercase tracking-wide">Zeitblöcke & Pausen</span>
                                                        <v-btn x-small depressed color="blue darken-4" class="blue--text text--lighten-3 font-weight-bold px-2 rounded-lg" @click="addTimeBlock(shift)">
                                                            <v-icon left x-small>mdi-plus</v-icon> Block
                                                        </v-btn>
                                                    </div>
                                                    
                                                    <div v-for="(time, tIndex) in shift.times" :key="'t'+tIndex" class="time-row d-flex align-center mb-2">
                                                        <!-- Segmented button toggle for Arbeit / Pause -->
                                                        <div class="d-inline-flex rounded-lg border-subtle mr-3" style="background: rgba(0,0,0,0.25); padding: 2px;">
                                                            <v-btn
                                                                x-small
                                                                depressed
                                                                :color="time.type === 'Arbeitszeit' ? 'blue accent-4' : 'transparent'"
                                                                :class="time.type === 'Arbeitszeit' ? 'white--text font-weight-bold' : 'grey--text text--lighten-1'"
                                                                class="rounded-md px-2"
                                                                style="height: 24px; min-width: 55px; text-transform: none; font-size: 0.72rem; letter-spacing: 0;"
                                                                @click="time.type = 'Arbeitszeit'"
                                                            >
                                                                Arbeit
                                                            </v-btn>
                                                            <v-btn
                                                                x-small
                                                                depressed
                                                                :color="time.type === 'Pause' ? 'grey darken-2' : 'transparent'"
                                                                :class="time.type === 'Pause' ? 'white--text font-weight-bold' : 'grey--text text--lighten-1'"
                                                                class="rounded-md px-2"
                                                                style="height: 24px; min-width: 55px; text-transform: none; font-size: 0.72rem; letter-spacing: 0;"
                                                                @click="time.type = 'Pause'"
                                                            >
                                                                Pause
                                                            </v-btn>
                                                        </div>
                                                        
                                                        <div class="d-inline-flex align-center time-capsule rounded-lg px-2 py-1 border-subtle">
                                                            <v-icon x-small color="grey" class="mr-1">mdi-clock-outline</v-icon>
                                                            <input type="time" v-model="time.start" class="time-input white--text" />
                                                            <span class="grey--text mx-1 opacity-50">-</span>
                                                            <input type="time" v-model="time.end" class="time-input white--text" />
                                                        </div>

                                                        <v-btn icon x-small color="red lighten-3" class="ml-2 opacity-hover" @click="removeTimeBlock(shift, tIndex)" :disabled="shift.times.length === 1">
                                                            <v-icon small>mdi-trash-can-outline</v-icon>
                                                        </v-btn>
                                                    </div>
                                                </v-col>

                                                <v-col cols="12" md="5" lg="6" class="pl-md-6">
                                                    <div class="text-caption font-weight-bold grey--text text--darken-1 text-uppercase tracking-wide mb-3">Zuweisung & Info</div>
                                                    
                                                    <v-autocomplete
                                                        v-model="shift.assignedUserId"
                                                        :items="mappedEmployees"
                                                        item-text="fullName"
                                                        item-value="userId" 
                                                        label="Mitarbeiter fixieren (Prio 1)"
                                                        dense
                                                        outlined
                                                        flat
                                                        hide-details
                                                        class="mb-3 rounded-lg"
                                                        background-color="grey darken-4"
                                                        placeholder="Automatisch (Algorithmus)"
                                                        clearable
                                                    >
                                                        <template v-slot:prepend-inner>
                                                            <v-icon small color="grey" class="mt-1">mdi-account</v-icon>
                                                        </template>
                                                        <template v-slot:item="{ item }">
                                                            <v-avatar size="24" class="mr-3"><img :src="getAvatarUrl(item.picture)"></v-avatar>
                                                            <span :class="{ 'red--text text--lighten-1': isEmployeeOnVacation(item, currentConfig.date) }">
                                                                {{ item.fullName }}
                                                                <span v-if="isEmployeeOnVacation(item, currentConfig.date)" class="font-weight-bold ml-1">[Ferien]</span>
                                                            </span>
                                                        </template>
                                                        <template v-slot:selection="{ item }">
                                                            <v-chip x-small :color="isEmployeeOnVacation(item, currentConfig.date) ? 'red lighten-1' : 'success'" label class="ma-0 black--text font-weight-bold">
                                                                {{ item.fullName }} {{ isEmployeeOnVacation(item, currentConfig.date) ? '[Ferien]' : '' }}
                                                            </v-chip>
                                                        </template>
                                                    </v-autocomplete>

                                                    <v-text-field
                                                        v-model="shift.note"
                                                        label="Notiz / Event-Name"
                                                        dense
                                                        outlined
                                                        flat
                                                        hide-details
                                                        background-color="grey darken-4"
                                                        class="rounded-lg"
                                                        placeholder="z.B. Tag der offenen Tür"
                                                    >
                                                        <template v-slot:prepend-inner>
                                                            <v-icon small color="grey" class="mt-1">mdi-text</v-icon>
                                                        </template>
                                                    </v-text-field>
                                                </v-col>
                                            </v-row>
                                        </div>
                                    </v-card>
                                </v-slide-y-transition>

                            </div>
                        </div>
                    </v-col>
                </v-row>
            </v-card-text>
            </v-container>
        </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import api from "@/api"

export default {
  name: "ShiftManagementMainDialogCreateSpecialDays",
  props: {
    studioId: { type: String, required: true },
    planningStartDate: { type: String, required: true },
    planningEndDate: { type: String, required: true },
    availableShifts: { type: Array, default: () => [] },
    employees: { type: Array, default: () => [] },
    weeklyTemplateData: { type: Array, default: () => [] },
    publicHolidays: { type: Array, default: () => [] },
  },
  data() {
    return {
      specialDays: [], 
      dialog: false,
      saving: false,
      loading: false, 
      deleting: null, // NEU: Für Lösch-Spinner
      menuDate: false,
      
      currentConfig: {
        date: null,
        mode: 'replace',
        shifts: []
      },
      standardShiftsForDate: [] 
    }
  },
  mounted() {
    this.fetchExistingSpecialDays();
  },
  watch: {
    planningStartDate: 'fetchExistingSpecialDays',
    planningEndDate: 'fetchExistingSpecialDays',
    studioId: 'fetchExistingSpecialDays'
  },
  computed: {
    totalHours() {
        let shiftsToCount = [...this.currentConfig.shifts];
        if (this.currentConfig.mode === 'add') {
             shiftsToCount = [...shiftsToCount, ...this.standardShiftsForDate];
        }

        return shiftsToCount.reduce((sum, s) => {
            if(!s.times) return sum;
            return sum + s.times.reduce((tSum, t) => {
                if (t.type === 'Arbeitszeit' && t.start && t.end) {
                    const start = this.$moment(t.start, "HH:mm");
                    const end = this.$moment(t.end, "HH:mm");
                    if(end.isBefore(start)) end.add(1, 'day');
                    return tSum + end.diff(start, 'hours', true);
                }
                return tSum;
            }, 0);
        }, 0);
    },
    totalShiftsCount() {
        if (this.currentConfig.mode === 'add') {
            return this.standardShiftsForDate.length + this.currentConfig.shifts.length;
        }
        return this.currentConfig.shifts.length;
    },
    // Alle Feiertage anzeigen (inkl. Wochenende)
    publicHolidaysInRange() {
      return (this.publicHolidays || [])
    },
    mappedEmployees() {
      return (this.employees || []).map(emp => {
        const userId = emp.user ? (typeof emp.user === 'object' ? emp.user._id : emp.user) : emp._id
        return {
          ...emp,
          userId: String(userId),
          fullName: emp.fullName || (emp.user ? `${emp.user.vorname} ${emp.user.nachname}` : ""),
          picture: emp.picture || ""
        }
      }).sort((a, b) => a.fullName.localeCompare(b.fullName))
    },
  },
  methods: {
    // NEU: Spezialtag löschen
    async deleteDay(day) {
        if(!confirm(`Möchtest du den ${this.$moment(day.date).format('DD.MM.YYYY')} wirklich zurücksetzen? Alle speziellen Schichten werden gelöscht und der Standard wiederhergestellt.`)) return;
        
        this.deleting = day.date;
        try {
            // Stelle sicher, dass diese Methode in deiner API definiert ist:
            // deleteSpecialDay(studio, date) => axios.delete(...)
            await api.shiftManagement.deleteSpecialDay(this.studioId, day.date);
            
            // Aus Liste entfernen
            this.specialDays = this.specialDays.filter(d => d.date !== day.date);
            this.$toast.success("Standard wiederhergestellt.");
        } catch (e) {
            console.error(e);
            this.$toast.error("Fehler beim Zurücksetzen.");
        } finally {
            this.deleting = null;
        }
    },

    // NEU: Spezialtag direkt für einen Feiertag öffnen
    openDialogForHoliday(holiday) {
      // Prüfen ob bereits ein Spezialtag existiert
      const existing = this.specialDays.find(d => d.date === holiday.date)
      if (existing) {
        this.editDay(existing)
      } else {
        this.openDialog(null)
        this.$nextTick(() => {
          this.currentConfig.date = holiday.date
          this.onDateOrModeChange()
        })
      }
    },

    async fetchExistingSpecialDays() {
        if(!this.studioId || !this.planningStartDate) return;
        
        this.loading = true;
        this.specialDays = [];

        try {
            const response = await api.shiftManagement.fetchPlan(this.studioId, this.planningStartDate, this.planningEndDate);
            const allShifts = response.shifts || response || [];
            const specialShifts = allShifts.filter(s => s.isSpecialDemand === true || (s.allocationNote && s.allocationNote.includes('Spezialtag')));

            const grouped = {};
            specialShifts.forEach(s => {
                if(!grouped[s.date]) {
                    const isAdd = s.allocationNote && s.allocationNote.includes('Zusatz');
                    grouped[s.date] = {
                        date: s.date,
                        mode: isAdd ? 'add' : 'replace',
                        shifts: []
                    };
                }
                
                grouped[s.date].shifts.push({
                    shiftTemplateId: s.shiftTemplate ? (s.shiftTemplate._id || s.shiftTemplate) : null,
                    assignedUserId: s.assignedEmployee ? (s.assignedEmployee._id || s.assignedEmployee) : null,
                    note: s.allocationNote,
                    times: s.times || []
                });
            });

            this.specialDays = Object.values(grouped);

        } catch(e) {
            console.error("Fehler beim Laden der Spezialtage", e);
        } finally {
            this.loading = false;
        }
    },

    getUserId(item) {
        if (!item || !item.user) return null;
        return typeof item.user === 'object' ? item.user._id : item.user;
    },
    getAvatarUrl(picture) {
      if (!picture) return "/img/default-avatar.png"
      if (picture.startsWith("http") || picture.startsWith("data:")) return picture
      if (picture.startsWith("/img/profilbilder")) return picture
      if (picture.startsWith("img/profilbilder")) return "/" + picture
      const cleanPic = picture.startsWith("/") ? picture : "/" + picture
      return "/img/profilbilder" + cleanPic
    },
    isEmployeeOnVacation(employee, dateStr) {
      if (!employee || !dateStr) return false
      const vacations = employee.vacations || []
      const targetDate = this.$moment(dateStr).format("YYYY-MM-DD")
      return vacations.some(vac => {
        return (vac.dates || []).some(d => {
          return this.$moment(d.date).format("YYYY-MM-DD") === targetDate
        })
      })
    },

    getShiftName(templateId) {
        if (!templateId) return "Spezial-Schicht";
        const tpl = this.availableShifts.find(s => s._id === templateId || s._id === templateId._id);
        return tpl ? tpl.name : "Unbekannte Vorlage";
    },
    getShiftTimeRange(shift) {
        if(!shift.times || shift.times.length === 0) return "?";
        return `${shift.times[0].start} - ${shift.times[shift.times.length-1].end}`;
    },

    openDialog(existingDay) {
        if (existingDay) {
            this.currentConfig = JSON.parse(JSON.stringify(existingDay));
            this.calculateStandardShifts(this.currentConfig.date);
        } else {
            this.currentConfig = {
                date: null,
                mode: 'replace', 
                shifts: [] 
            };
            this.standardShiftsForDate = [];
        }
        this.dialog = true;
    },

    onDateSelected(val) {
        this.menuDate = false;
        this.onDateOrModeChange();
    },

    onDateOrModeChange() {
        if (!this.currentConfig.date) return;

        this.calculateStandardShifts(this.currentConfig.date);

        if (this.currentConfig.mode === 'replace') {
            if (this.currentConfig.shifts.length === 0) {
                this.loadStandardShifts();
            }
        } else {
            const hasNonStandard = this.currentConfig.shifts.some(s => !s.isStandard);
            if (!hasNonStandard) {
                this.currentConfig.shifts = [];
            }
        }
    },

    calculateStandardShifts(dateStr) {
        const isoWeekday = this.$moment(dateStr).isoWeekday();
        const dayTemplate = this.weeklyTemplateData.find(d => d.dayOfWeek === isoWeekday);
        
        this.standardShiftsForDate = [];
        
        if (dayTemplate && dayTemplate.requiredShifts) {
            this.standardShiftsForDate = dayTemplate.requiredShifts.map(req => {
                const tpl = this.availableShifts.find(s => s._id === req.shiftTemplateId._id || s._id === req.shiftTemplateId);
                let times = [{ start: '00:00', end: '00:00', type: 'Arbeitszeit' }];
                if (tpl && tpl.times) times = JSON.parse(JSON.stringify(tpl.times));

                return {
                    shiftTemplateId: req.shiftTemplateId._id || req.shiftTemplateId,
                    assignedUserId: null,
                    note: '',
                    times: times,
                    isStandard: true 
                };
            });
        }
    },

    loadStandardShifts() {
        this.currentConfig.shifts = JSON.parse(JSON.stringify(this.standardShiftsForDate));
        this.$toast.info("Standard-Schichten geladen");
    },

    addShift() {
        this.currentConfig.shifts.push({
            shiftTemplateId: null,
            assignedUserId: null,
            note: '',
            times: [{ start: '08:00', end: '12:00', type: 'Arbeitszeit' }]
        });
    },

    addTimeBlock(shift) {
        const last = shift.times[shift.times.length - 1];
        const newType = last.type === 'Arbeitszeit' ? 'Pause' : 'Arbeitszeit';
        shift.times.push({ 
            start: last.end, 
            end: this.$moment(last.end, "HH:mm").add(30, 'minutes').format("HH:mm"), 
            type: newType 
        });
    },

    removeTimeBlock(shift, index) {
        shift.times.splice(index, 1);
    },

    copyShift(shift) {
        const copy = JSON.parse(JSON.stringify(shift));
        copy.assignedUserId = null; 
        this.currentConfig.shifts.push(copy);
    },

    removeShift(index) {
        this.currentConfig.shifts.splice(index, 1);
    },

    applyTemplate(shift, templateId) {
        const template = this.availableShifts.find(t => t._id === templateId);
        if (template && template.times && template.times.length > 0) {
            shift.times = JSON.parse(JSON.stringify(template.times));
        }
    },

    async saveCurrentDay() {
        if(!this.currentConfig.date) return this.$toast.error("Bitte Datum wählen");
        if(this.currentConfig.mode === 'add' && this.currentConfig.shifts.length === 0) {
             return this.$toast.error("Für 'Zusatz' bitte mindestens eine Schicht definieren.");
        }

        this.saving = true;
        try {
            const payload = {
                studio: this.studioId,
                date: this.currentConfig.date,
                mode: this.currentConfig.mode,
                shifts: this.currentConfig.shifts
            };

            await api.shiftManagement.createSpecialDay(payload);
            
            const existingIndex = this.specialDays.findIndex(d => d.date === this.currentConfig.date);
            if (existingIndex >= 0) {
                this.$set(this.specialDays, existingIndex, this.currentConfig);
            } else {
                this.specialDays.push(this.currentConfig);
            }

            this.$toast.success("Spezialtag gespeichert");
            this.dialog = false;

        } catch (e) {
            console.error(e);
            this.$toast.error("Fehler beim Speichern");
        } finally {
            this.saving = false;
        }
    },
    
    editDay(day) {
        this.openDialog(day);
    }
  }
}
</script>

<style scoped>
/* GENERAL UTILS */
.border-subtle { border: 1px solid rgba(255,255,255,0.08) !important; }
.border-top-subtle { border-top: 1px solid rgba(255,255,255,0.08) !important; }
.border-bottom-subtle { border-bottom: 1px solid rgba(255,255,255,0.08) !important; }
.border-right-subtle { border-right: 1px solid rgba(255,255,255,0.08) !important; }
.border-dashed { border: 2px dashed rgba(255,255,255,0.15) !important; }

/* HOVER EFFECTS */
.hover-glow { transition: 0.3s; }
.hover-glow:hover { box-shadow: 0 0 15px rgba(66, 165, 245, 0.2); border-color: rgba(66, 165, 245, 0.5) !important; }
.opacity-hover { opacity: 0.5; transition: 0.2s; }
.opacity-hover:hover { opacity: 1; }
.opacity-50 { opacity: 0.5; }
.opacity-60 { opacity: 0.6; }

/* LAYOUT HELPERS */
.relative { position: relative; }
.bg-stripes {
    background: repeating-linear-gradient(45deg, #252525, #252525 10px, #2a2a2a 10px, #2a2a2a 20px);
}
@media (min-width: 960px) {
    .border-right-responsive { border-right: 1px solid rgba(255,255,255,0.08); }
}

.time-input {
    background: transparent;
    border: none;
    color: white;
    width: 50px;
    text-align: center;
    font-family: 'Outfit', 'Inter', monospace;
    font-weight: 600;
    font-size: 0.95rem;
    outline: none;
    padding: 0;
}
.time-input::-webkit-calendar-picker-indicator {
    display: none !important;
    -webkit-appearance: none;
}
.time-input::-webkit-inner-spin-button,
.time-input::-webkit-clear-button {
    display: none !important;
    -webkit-appearance: none;
}

.time-capsule {
    background-color: rgba(0, 0, 0, 0.4) !important;
    border: 1px solid rgba(255, 255, 255, 0.08) !important;
    transition: border-color 0.2s, box-shadow 0.2s;
}
.time-capsule:focus-within {
    border-color: #2979ff !important;
    box-shadow: 0 0 8px rgba(41, 121, 255, 0.25);
}

.mode-card-active-replace {
    background: linear-gradient(135deg, rgba(21, 101, 192, 0.2) 0%, rgba(21, 101, 192, 0.05) 100%) !important;
    border: 1px solid #1976d2 !important;
    box-shadow: 0 0 10px rgba(25, 118, 210, 0.15);
}
.mode-card-active-add {
    background: linear-gradient(135deg, rgba(46, 125, 50, 0.2) 0%, rgba(46, 125, 50, 0.05) 100%) !important;
    border: 1px solid #388e3c !important;
    box-shadow: 0 0 10px rgba(56, 142, 60, 0.15);
}

/* SHIFT CARD SPECIFIC */
.shift-card {
    border-radius: 12px;
    border: 1px solid rgba(255, 255, 255, 0.08) !important;
    background-color: #1e1e1e !important;
    overflow: hidden;
    transition: border-color 0.3s, box-shadow 0.3s;
}
.shift-card:hover {
    border-color: rgba(66, 165, 245, 0.25) !important;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.4);
}
.shift-card-header {
    background: #252525;
    border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}
.small-select >>> .v-input__control { min-height: 32px !important; }
.small-select >>> .v-input__slot { padding: 0 12px !important; min-height: 32px !important; }

/* SCROLLBAR */
.custom-scrollbar::-webkit-scrollbar { width: 8px; }
.custom-scrollbar::-webkit-scrollbar-track { background: #1e1e1e; }
.custom-scrollbar::-webkit-scrollbar-thumb { background: #424242; border-radius: 4px; }
.custom-scrollbar::-webkit-scrollbar-thumb:hover { background: #616161; }

.tracking-wide { letter-spacing: 1px; }
</style>
